import {
  createContext,
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";

import { PaymentApiType, TransactionApiType } from "../../../../util/types";
import { usePagination } from "../../../../hooks/usePagination";
import { ISortConfig } from "../../../../components/ui-kit/ReactTable/context/TableContext";
import { useTransactionsList } from "../transactions/hooks/useTransactionsList";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { useUrlFilters, useUrlParam } from "../../../../hooks/useUrlState";
import { useUpdateEffect } from "../../../../hooks/useUpdateEffect";
import {
  getDateFromTimeFilter,
  DEFAULT_TIME_FILTER,
} from "../transactions/utils/timeFilterUtils";

/** Query param holding the Incoming / Outgoing tab (kept apart from the filters). */
const DIRECTION_PARAM = "direction";

interface ITransactionsContext {
  transactionsData: TransactionApiType[];
  refreshTransactions: () => void;
  setTransactionsFilters: (filters: Record<string, any>) => void;
  onPaginationChange: (pageValue: number, limitValue: number) => void;
  resetPageNum: (pageValue: number) => void;
  onSortChange: (val?: ISortConfig) => void;
  loading: boolean;
  totalDocs: number;
  page: number;
  limit: number;
  filters: Record<string, any>;
  autoResetPageIndex?: boolean;
  setDirection: (val: string | undefined) => void;
  direction?: string;
  setOwner: (companyId: string | undefined) => void;
  setSearchParams: SetURLSearchParams;
}

const TransactionsContext = createContext<ITransactionsContext>({
  transactionsData: [],
  refreshTransactions: () => null,
  loading: false,
  setTransactionsFilters: () => null,
  onPaginationChange: () => null,
  onSortChange: () => null,
  setDirection: () => null,
  resetPageNum: () => null,
  totalDocs: 0,
  page: 1,
  limit: 25,
  filters: {},
  setOwner: () => null,
  setSearchParams: () => null,
});

export const TransactionsContextProvider: FC<{
  children: ReactElement | ReactElement[];
  recurring?: boolean;
  vrp?: boolean;
  provider?: string;
  /** Top-level transactions pages: filters, direction tab + page live in the URL. */
  syncWithUrl?: boolean;
}> = ({ children, recurring, vrp, provider, syncWithUrl = false }) => {
  const [, setSearchParams] = useSearchParams();
  // Computed once per mount: useUrlFilters needs a stable `defaults` reference.
  const [defaultFilters] = useState<Record<string, any>>(() => {
    // The default time window only makes sense where the UI exposes the
    // "Time range" dropdown (OB / Other lists). Recurring and VRP lists have
    // no such control, so a hidden `from` there just made them look empty.
    if (recurring || vrp) return {};
    const initialFromDate = getDateFromTimeFilter(DEFAULT_TIME_FILTER);
    return initialFromDate ? { from: initialFromDate.toISOString() } : {};
  });
  const [filters, setFilters] = useUrlFilters<Record<string, any>>(
    defaultFilters,
    { reserved: [DIRECTION_PARAM], enabled: syncWithUrl }
  );
  const { page, limit, onPaginationChange, resetPageNum } = usePagination({
    syncWithUrl,
  });
  const [direction, setDirection] = useUrlParam(DIRECTION_PARAM, {
    enabled: syncWithUrl,
  });
  const [sort, setSort] = useState<ISortConfig>({
    field: "createdAt",
    direction: "DESC",
  });
  const [companyId, setCompanyId] = useState<string>();
  const { transactionsData, loading, totalDocs, refreshTransactions } =
    useTransactionsList(
      page,
      limit,
      direction,
      sort,
      filters,
      companyId,
      recurring,
      provider,
      vrp
    );
  const [transactions, setTransactions] = useState<TransactionApiType[]>([]);

  useEffect(() => {
    setTransactions(transactionsData);
  }, [transactionsData]);

  // A new filter / tab / owner starts from page 1 — otherwise a narrower
  // result set leaves the user on a page that no longer exists.
  useUpdateEffect(() => {
    resetPageNum();
  }, [JSON.stringify(filters), direction, companyId]);

  useEffect(() => {
    if (page > 1) {
      const maxPage = Math.ceil(totalDocs / limit);
      if (maxPage > 0 && page > maxPage) {
        resetPageNum();
      }
    }
  }, [totalDocs, page, limit]);

  return (
    <TransactionsContext.Provider
      value={{
        transactionsData: transactions,
        refreshTransactions,
        loading,
        totalDocs,
        page,
        limit,
        filters,
        onPaginationChange,
        onSortChange: setSort,
        resetPageNum,
        setTransactionsFilters: setFilters,
        setDirection,
        direction,
        setOwner: setCompanyId,
        setSearchParams,
      }}
    >
      {children}
    </TransactionsContext.Provider>
  );
};

export const useTransactionsContext = () => {
  return useContext(TransactionsContext);
};
